import { useState } from "react";
import {
  Bot,
  Power,
  PowerOff,
  MoreHorizontal,
  Copy,
  Trash2,
  PencilLine,
  BarChart3,
  MessageSquare,
  Settings2,
  Clock,
} from "lucide-react";
import { cn } from "../../../../utils/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/UI/Popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../../../components/UI/Dialog";
import {
  eventTypeGradient,
  eventTypeIcon,
} from "../../utils/eventType";
import { formatRelative } from "../../utils/formatRelative";
import BotOverviewTab from "../BotOverviewTab";
import BotMessagesTimeline from "../BotMessagesTimeline";
import BotConfigForm from "../BotConfigForm";

const TABS = [
  { value: "overview", label: "Tổng quan", icon: BarChart3 },
  { value: "messages", label: "Tin nhắn", icon: MessageSquare },
  { value: "config", label: "Cài đặt", icon: Settings2 },
];

/**
 * Right detail panel — shows bot header + 3 tabs.
 * Empty / new-bot states are handled inline so the page doesn't need to know.
 */
const BotDetailPanel = ({
  bot,
  isNew,
  hasBots,
  activeTab = "overview",
  onTabChange,
  eventTypes,
  shopsList,
  tagsList,
  zaloTagsList,
  onUpdate,
  onDelete,
  onDuplicate,
  onSaveNew,
  onCancelNew,
  onCreateNew,
  chatbotType = "event",
  duplicateSeed,
}) => {
  if (isNew) {
    return (
      <main className="flex-1 flex flex-col h-full bg-gray-50 overflow-hidden">
        <NewBotHeader chatbotType={chatbotType} onCancel={onCancelNew} />
        <div className="flex-1 overflow-y-auto">
          <BotConfigForm
            mode="create"
            initial={duplicateSeed || null}
            eventTypes={eventTypes}
            shopsList={shopsList}
            tagsList={tagsList}
            zaloTagsList={zaloTagsList}
            chatbotType={chatbotType}
            onSubmit={onSaveNew}
            onCancel={onCancelNew}
          />
        </div>
      </main>
    );
  }

  if (!bot) {
    return (
      <main className="flex-1 flex flex-col h-full bg-gray-50 items-center justify-center">
        <EmptySelection hasBots={hasBots} onCreateNew={onCreateNew} />
      </main>
    );
  }

  return (
    <main className="flex-1 flex flex-col h-full bg-gray-50 overflow-hidden">
      <BotHeader
        bot={bot}
        eventTypes={eventTypes}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        onEdit={() => onTabChange("config")}
      />

      <TabBar
        activeTab={activeTab}
        onChange={onTabChange}
      />

      <div className="flex-1 overflow-y-auto">
        {activeTab === "overview" && (
          <BotOverviewTab
            bot={bot}
            eventTypes={eventTypes}
            shopsList={shopsList}
            chatbotType={chatbotType}
            onSwitchTab={onTabChange}
          />
        )}
        {activeTab === "messages" && (
          <BotMessagesTimeline
            bot={bot}
            shopsList={shopsList}
          />
        )}
        {activeTab === "config" && (
          <BotConfigForm
            mode="edit"
            initial={bot}
            eventTypes={eventTypes}
            shopsList={shopsList}
            tagsList={tagsList}
            zaloTagsList={zaloTagsList}
            chatbotType={chatbotType}
            onSubmit={(data) => onUpdate(bot.id, data)}
          />
        )}
      </div>
    </main>
  );
};

const BotHeader = ({ bot, eventTypes, onUpdate, onDelete, onDuplicate, onEdit }) => {
  const Icon = eventTypeIcon(bot.eventType);
  const gradient = eventTypeGradient(bot.eventType);
  const eventTypeName =
    eventTypes.find((e) => e.action === bot.eventType)?.actionName ||
    bot.eventType ||
    "—";
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <header className="px-6 py-4 bg-white border-b border-gray-200 flex items-start gap-4">
      <div
        className={cn(
          "w-14 h-14 shrink-0 rounded-2xl bg-gradient-to-br flex items-center justify-center shadow",
          gradient,
        )}
      >
        <Icon size={26} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 truncate">
              {bot.description || (
                <span className="italic text-gray-400">(Chưa có mô tả)</span>
              )}
            </h1>
            <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1">
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ backgroundColor: "currentColor" }}
                />
                {eventTypeName}
              </span>
              <span>·</span>
              <span className="inline-flex items-center gap-1">
                <Clock size={11} />
                Tạo {formatRelative(bot.createdAt)}
              </span>
              {bot.updatedAt && bot.updatedAt !== bot.createdAt && (
                <>
                  <span>·</span>
                  <span>Cập nhật {formatRelative(bot.updatedAt)}</span>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <ActiveToggle
              active={bot.isActive}
              onToggle={() =>
                onUpdate(bot.id, { isActive: !bot.isActive }, { silent: true })
              }
            />
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-8 h-8 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                >
                  <MoreHorizontal size={18} />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-44 p-1" align="end">
                <MenuItem icon={PencilLine} label="Chỉnh sửa" onClick={onEdit} />
                <MenuItem
                  icon={Copy}
                  label="Nhân bản"
                  onClick={() => onDuplicate(bot)}
                />
                <div className="my-1 h-px bg-gray-100" />
                <MenuItem
                  icon={Trash2}
                  label="Xóa bot"
                  danger
                  onClick={() => setConfirmDelete(true)}
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Xóa bot này?</DialogTitle>
            <DialogDescription>
              Bot &quot;{bot.description || "Chưa có mô tả"}&quot; sẽ bị xóa
              vĩnh viễn. Lịch sử tin nhắn đã gửi vẫn được giữ lại trong hệ
              thống.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setConfirmDelete(false)}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-200 hover:bg-gray-50"
            >
              Hủy
            </button>
            <button
              type="button"
              onClick={() => {
                setConfirmDelete(false);
                onDelete(bot.id);
              }}
              className="px-3 py-1.5 text-sm rounded-md bg-red-600 text-white hover:bg-red-700"
            >
              Xóa bot
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </header>
  );
};

const ActiveToggle = ({ active, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className={cn(
      "inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors",
      active
        ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 ring-1 ring-emerald-200"
        : "bg-gray-100 text-gray-600 hover:bg-gray-200 ring-1 ring-gray-200",
    )}
    title={active ? "Click để tắt" : "Click để bật"}
  >
    {active ? <Power size={12} /> : <PowerOff size={12} />}
    {active ? "Đang hoạt động" : "Đã tắt"}
  </button>
);

const MenuItem = ({ icon: Icon, label, onClick, danger }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "w-full text-left px-2.5 py-1.5 text-sm rounded-md inline-flex items-center gap-2 transition-colors",
      danger
        ? "text-red-600 hover:bg-red-50"
        : "text-gray-700 hover:bg-gray-50",
    )}
  >
    <Icon size={14} />
    {label}
  </button>
);

const TabBar = ({ activeTab, onChange }) => (
  <nav className="px-6 bg-white border-b border-gray-200 flex items-center gap-0">
    {TABS.map((tab) => {
      const TabIcon = tab.icon;
      const active = activeTab === tab.value;
      return (
        <button
          key={tab.value}
          type="button"
          onClick={() => onChange(tab.value)}
          className={cn(
            "relative px-4 py-2.5 text-sm font-medium transition-colors inline-flex items-center gap-1.5 -mb-px",
            active
              ? "text-indigo-600 border-b-2 border-indigo-500"
              : "text-gray-500 hover:text-gray-700 border-b-2 border-transparent",
          )}
        >
          <TabIcon size={14} />
          {tab.label}
        </button>
      );
    })}
  </nav>
);

const NewBotHeader = ({ chatbotType, onCancel }) => (
  <header className="px-6 py-4 bg-white border-b border-gray-200 flex items-center gap-4">
    <div className="w-14 h-14 shrink-0 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow">
      <Bot size={26} className="text-white" />
    </div>
    <div className="flex-1">
      <h1 className="text-lg font-semibold text-gray-900">Tạo bot mới</h1>
      <div className="text-xs text-gray-500 mt-0.5">
        {chatbotType === "keyword"
          ? "Bot sẽ tự động trả lời khi có từ khóa khớp"
          : "Bot sẽ tự động gửi tin nhắn khi sự kiện xảy ra"}
      </div>
    </div>
    <button
      type="button"
      onClick={onCancel}
      className="px-3 py-1.5 text-sm rounded-md border border-gray-200 hover:bg-gray-50"
    >
      Hủy
    </button>
  </header>
);

const EmptySelection = ({ hasBots, onCreateNew }) => (
  <div className="text-center max-w-sm">
    <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mb-4">
      <Bot size={36} className="text-indigo-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-900">
      {hasBots ? "Chọn bot để xem chi tiết" : "Tạo bot đầu tiên"}
    </h2>
    <p className="text-sm text-gray-500 mt-2">
      {hasBots
        ? "Bấm vào 1 bot ở danh sách bên trái để xem tổng quan, lịch sử tin nhắn đã gửi và chỉnh sửa cấu hình."
        : "Bot sẽ tự động gửi tin nhắn cho khách hàng khi sự kiện được kích hoạt — chẳng hạn khi khách thêm tag VIP, vào nhóm, hoặc nhắn từ khóa."}
    </p>
    {!hasBots && (
      <button
        type="button"
        onClick={onCreateNew}
        className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md shadow-sm"
      >
        Tạo bot mới
      </button>
    )}
  </div>
);

export default BotDetailPanel;
